/* eslint-disable react-hooks/exhaustive-deps */
import { zodResolver } from '@hookform/resolvers/zod'
import { useFetcher, useRevalidator, useSearchParams } from '@remix-run/react'
import { type TFunction } from 'i18next'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'
import { ScrollArea } from '#app/components/ui/scroll-area.tsx'
import { ClientOnly } from '#app/hooks/use-client-only.tsx'
import { useGlobalChatStore } from '#app/routes/chat+/index.tsx'
import { ConversationContainer } from '../chat-containers.tsx'
import { EmptyChat } from '../empty-chat.tsx'
import MenuBar from '../menu-bar/index.tsx'
import { MessageList } from '../message-list.tsx'
import { MessageFetcherForm } from './components/message-fetcher-form.tsx'
import { ScrollDown } from './components/scroll-down.tsx'
import { useHandleNewAssistantMessage } from './hooks/use-handle-new-assistant-message.tsx'
import { useIntersectionObserver } from './hooks/use-intersection-observer.tsx'
import { useScrollToBottom } from './hooks/use-scroll-to-bottom.tsx'
import { useSetTitle } from './hooks/use-set-title.tsx'
import { useStreamingUpdates } from './hooks/use-streaming-updates.tsx'
import { getMessageBasedOnType } from './utils/getMessageBasedOnType.ts'
import { onInvalid } from './utils/onSubmit.ts'

interface FormTranslations {
	wiserBotMessage: string
}

const RequestFeatureFormSchema = (translations: FormTranslations) =>
	z.object({
		wiserBotMessage: z
			.string({ required_error: translations.wiserBotMessage })
			.max(20000, 'Cannot exceed 20000 characters')
			.refine(value => value.replace(/\s/g, '').length >= 2, {
				message: 'Must have at least 2 non-whitespace characters',
			}),
	})

const formErrorsLoader = (t: TFunction): FormTranslations => {
	return { wiserBotMessage: t('feature') }
}

const Conversation: React.FC = () => {
	const { t } = useTranslation('contact')
	const formErrors = formErrorsLoader(t)
	const messageFetcher: any = useFetcher()
	const titleFetcher: any = useFetcher()
	const [searchParams] = useSearchParams()
	let revalidator = useRevalidator()

	const typeOfMessage = searchParams.get('typeOfMessage')
	const coinsLeft = useGlobalChatStore(state => state.coinsLeft)
	const dispatch = useGlobalChatStore(useShallow(state => state.dispatch))
	const messages = useGlobalChatStore(useShallow(state => state.messages))
	const conversationId = useGlobalChatStore(state => state.conversationId)

	const showEmptyChat = useGlobalChatStore(state => state.showEmptyChat)
	const currentMessageProcess = useGlobalChatStore(
		state => state.currentMessageProcess,
	)
	const typeOfView = useGlobalChatStore(state => state.typeOfView)

	const error = useGlobalChatStore(state => state.error)
	const titleHasBeenSet = useGlobalChatStore(state => state.titleHasBeenSet)
	const isCurrentlyStreaming = useGlobalChatStore(
		state => state.isCurrentlyStreaming,
	)
	const eventSourceRef = useRef<EventSource | null>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const bottomOfChatRef = useRef<HTMLDivElement>(null)

	const [isBottomVisible, setIsBottomVisible] = useState(true)
	const isSubmitting = Boolean(messageFetcher.state === 'submitting')
	const isEmptyChat = messages.length === 0

	const {
		register,
		handleSubmit,
		formState: { isValid },
		trigger,
		setValue,
		resetField,
	} = useForm({
		defaultValues: {
			wiserBotMessage: '',
		},
		resolver: zodResolver(RequestFeatureFormSchema(formErrors)),
	})

	useScrollToBottom(bottomOfChatRef, [conversationId, isSubmitting, messages])

	useIntersectionObserver({
		ref: bottomOfChatRef,
		callback: isVisible => {
			setIsBottomVisible(isVisible)
		},
		threshold: 0.1,
	})

	useHandleNewAssistantMessage({
		messageFetcher,
		isCurrentlyStreaming,
		messages,
		conversationId,
		titleFetcher,
	})

	useSetTitle({
		titleFetcher,
		titleHasBeenSet,
	})

	useStreamingUpdates({
		isCurrentlyStreaming,
		conversationId,
		dispatch,
		revalidator,
		eventSourceRef,
	})

	useEffect(() => {
		if (messageFetcher.data && !isCurrentlyStreaming) {
			if (messageFetcher.data?.newId === currentMessageProcess) {
				dispatch({
					type: 'SET_IS_CURRENTLY_STREAMING',
					isCurrentlyStreaming: true,
				})
			}
		}
	}, [messageFetcher.state])

	useEffect(() => {
		if (typeOfMessage && coinsLeft > 0) {
			const message = getMessageBasedOnType(typeOfMessage, t)

			if (message) {
				dispatch({ type: 'SET_TYPE_OF_VIEW', typeOfView: 'chat' })
			}
		}
	}, [typeOfMessage, coinsLeft])

	return (
		<ConversationContainer>
			<ClientOnly>{() => <MenuBar />}</ClientOnly>

			{messages.length === 0 && (
				<EmptyChat messageFetcher={messageFetcher} textareaRef={textareaRef} />
			)}
			{typeOfView !== 'session' && (
				<>
					<ScrollArea className="h-full">
						{messages.length !== 0 && <MessageList />}
						<div ref={bottomOfChatRef} className="mb-2"></div>
					</ScrollArea>
					{!isCurrentlyStreaming && (
						<ScrollDown
							isBottomVisible={isBottomVisible}
							showEmptyChat={showEmptyChat}
							bottomOfChatRef={bottomOfChatRef}
						/>
					)}
					<MessageFetcherForm
						isEmptyChat={isEmptyChat}
						error={error}
						isValid={isValid}
						isSubmitting={isSubmitting}
						textareaRef={textareaRef}
						eventSourceRef={eventSourceRef}
						register={register}
						trigger={trigger}
						setValue={setValue}
						showEmptyChat={showEmptyChat}
						dispatch={dispatch}
						handleSubmit={handleSubmit}
						messageFetcher={messageFetcher}
						conversationId={conversationId}
						resetField={resetField}
						onInvalid={onInvalid}
					/>
				</>
			)}
		</ConversationContainer>
	)
}

export default Conversation
